/**
 * Primitives Plotly : fond survol DAPI, axes / légende type Optim Turpe, templates tooltip.
 *
 * Typographie : `plotly/homeysPlotlyTheme.ts`.
 * Pour composer des graphiques métiers, préférer `@modules/data/utils/plotly`.
 */

import {
    HOMEYS_PLOTLY_AXES_LINE_COLOR,
    HOMEYS_PLOTLY_AXIS_TITLE_FONT,
    HOMEYS_PLOTLY_FIGURE_BACKGROUND,
    HOMEYS_PLOTLY_GRID_COLOR,
    HOMEYS_PLOTLY_HOVERLABEL_FONT,
    HOMEYS_PLOTLY_LEGEND_BACKGROUND,
    HOMEYS_PLOTLY_LEGEND_FONT,
    HOMEYS_PLOTLY_TICK_LENGTH,
    HOMEYS_PLOTLY_TICK_WIDTH,
    HOMEYS_PLOTLY_TOOLTIP_BACKGROUND,
    HOMEYS_PLOTLY_TOOLTIP_BORDER_COLOR,
    mergeHomeysPlotlyAxisTickFont,
} from "./plotly/homeysPlotlyTheme"

/** Format d’ordonnée par défaut dans les tooltips Plotly (2 décimales). */
const DEFAULT_HOVER_Y_FORMAT = ".2f"

const HOVER_Y_FORMAT_PATTERN = /^(?:\.[0-9]{1,2}f|,\.[0-9]{1,2}f)$/

const DAPI_LIKE_HOVER_BASE: Record<string, unknown> = {
    paper_bgcolor: HOMEYS_PLOTLY_FIGURE_BACKGROUND,
    plot_bgcolor: HOMEYS_PLOTLY_FIGURE_BACKGROUND,
    hovermode: "x unified",
    hoverlabel: {
        bgcolor: HOMEYS_PLOTLY_TOOLTIP_BACKGROUND,
        bordercolor: HOMEYS_PLOTLY_TOOLTIP_BORDER_COLOR,
        font: { ...HOMEYS_PLOTLY_HOVERLABEL_FONT },
    },
}

/** Lignes d’axes, grille et graduations (`light.layout.axes` + tick.marker). */
const OPTIM_TURPE_AXIS_DISPLAY: Record<string, unknown> = {
    showline: true,
    linewidth: 1,
    linecolor: HOMEYS_PLOTLY_AXES_LINE_COLOR,
    mirror: false,
    showgrid: true,
    gridcolor: HOMEYS_PLOTLY_GRID_COLOR,
    zeroline: false,
    ticks: "outside",
    ticklen: HOMEYS_PLOTLY_TICK_LENGTH,
    tickwidth: HOMEYS_PLOTLY_TICK_WIDTH,
    tickcolor: HOMEYS_PLOTLY_AXES_LINE_COLOR,
}

function optimTurpeAxisTitleObject(
    text: string | undefined,
    standoff = 10
):
    | {
          text: string
          standoff: number
          font: typeof HOMEYS_PLOTLY_AXIS_TITLE_FONT
      }
    | undefined {
    const t = text?.trim()
    if (!t) return undefined
    return {
        text: t,
        standoff,
        font: { ...HOMEYS_PLOTLY_AXIS_TITLE_FONT },
    }
}

function resolveOptimTurpeAxisTitle(
    chartLabel: string | undefined,
    previousTitle: unknown
): ReturnType<typeof optimTurpeAxisTitleObject> {
    const fromChart = optimTurpeAxisTitleObject(chartLabel)
    if (fromChart) return fromChart
    if (typeof previousTitle === "string" && previousTitle.trim()) {
        return optimTurpeAxisTitleObject(previousTitle.trim())
    }
    if (
        previousTitle &&
        typeof previousTitle === "object" &&
        typeof (previousTitle as { text?: string }).text === "string" &&
        (previousTitle as { text: string }).text.trim()
    ) {
        return optimTurpeAxisTitleObject(
            (previousTitle as { text: string }).text.trim()
        )
    }
    return undefined
}

function stripEmptyAxisTitle(
    axis: Record<string, unknown>
): Record<string, unknown> {
    const out = { ...axis }
    const t = out.title
    if (t === "" || t == null) {
        delete out.title
    } else if (
        typeof t === "object" &&
        t &&
        typeof (t as { text?: string }).text === "string" &&
        !(t as { text: string }).text.trim()
    ) {
        delete out.title
    }
    return out
}

export type PlotlyLayoutAxisChartMeta = {
    x_label?: string
    y_label?: string
    name?: string
}

export type EnrichOptimTurpeLayoutExtras = {
    /**
     * Axe X en dates avec formats jj/mm/aaaa (équiv. graphique « historique_puissance »).
     * À utiliser pour les séries temporelles quotidiennes hors Optim Turpe strict.
     */
    dateXAxisFr?: boolean
}

/**
 * Marges + axes visibles + titres X/Y + légende horizontale (style Optim Turpe / DAPI).
 */
export function enrichLayoutWithOptimTurpeAxes(
    layout: Record<string, unknown>,
    chart: PlotlyLayoutAxisChartMeta,
    extras?: EnrichOptimTurpeLayoutExtras
): Record<string, unknown> {
    const prevX = stripEmptyAxisTitle(
        (layout.xaxis as Record<string, unknown>) ?? {}
    )
    const prevY = stripEmptyAxisTitle(
        (layout.yaxis as Record<string, unknown>) ?? {}
    )
    const prevLegend = (layout.legend as Record<string, unknown>) ?? {}

    const useDateXFr =
        extras?.dateXAxisFr === true ||
        chart.name?.toLowerCase() === "historique_puissance"

    const xLabelSource = useDateXFr ? "Date" : chart.x_label
    const xTitle = resolveOptimTurpeAxisTitle(xLabelSource, prevX.title)
    const yTitle = resolveOptimTurpeAxisTitle(chart.y_label, prevY.title)

    const xaxisMerged: Record<string, unknown> = {
        ...OPTIM_TURPE_AXIS_DISPLAY,
        ...prevX,
    }
    if (xTitle) {
        xaxisMerged.title = {
            ...xTitle,
            standoff: Math.max(xTitle.standoff ?? 10, 14),
        }
    }

    if (useDateXFr) {
        xaxisMerged.type = "date"
        xaxisMerged.tickformat = "%d/%m/%Y"
        xaxisMerged.hoverformat = "%d/%m/%Y"
    }

    const yaxisMerged: Record<string, unknown> = {
        ...OPTIM_TURPE_AXIS_DISPLAY,
        ...prevY,
    }
    if (yTitle) yaxisMerged.title = yTitle

    const xaxisWithTicks = mergeHomeysPlotlyAxisTickFont(xaxisMerged)
    const yaxisWithTicks = mergeHomeysPlotlyAxisTickFont(yaxisMerged)

    const legendMerged: Record<string, unknown> = {
        ...prevLegend,
        orientation: "h",
        x: 0.5,
        xanchor: "center",
        y: -0.2,
        yanchor: "top",
        yref: "paper",
        xref: "paper",
        /** Clic sur un item de légende : afficher / masquer le tracé (comportement Plotly par défaut). */
        itemclick: "toggle",
        /** Double-clic : isoler ce tracé (masquer les autres), re-double-clic pour tout réafficher. */
        itemdoubleclick: "toggleothers",
        bgcolor: HOMEYS_PLOTLY_LEGEND_BACKGROUND,
        font: {
            ...HOMEYS_PLOTLY_LEGEND_FONT,
            ...(typeof prevLegend.font === "object" && prevLegend.font
                ? (prevLegend.font as Record<string, unknown>)
                : {}),
        },
    }

    const layoutOut: Record<string, unknown> = {
        ...layout,
        margin: { l: 68, r: 28, t: 20, b: 40 },
        xaxis: xaxisWithTicks,
        yaxis: yaxisWithTicks,
        legend: legendMerged,
    }
    if (useDateXFr) {
        layoutOut.separators = `,${"\u202f"}`
    }
    return layoutOut
}

export function validateHoverYFormat(value: string | undefined): string {
    if (value == null || typeof value !== "string") {
        return DEFAULT_HOVER_Y_FORMAT
    }
    const s = value.trim()
    if (HOVER_Y_FORMAT_PATTERN.test(s)) return s
    return DEFAULT_HOVER_Y_FORMAT
}

/**
 * Ex. `Puissance apparente (kVA)` → `kVA` pour le tooltip uniquement.
 */
export function extractTooltipUnitFromAxisLabel(yLabel: string): string {
    const s = yLabel.trim()
    if (!s) return ""
    const inParens = s.match(/\(([^)]+)\)\s*$/)
    if (inParens) return inParens[1].trim()
    return s
}

/**
 * Équivalent `get_tooltip_chart_template` (tooltip Y + unité).
 */
export function buildTooltipChartTemplate(options: {
    yFormat: string
    unit: string
    hasCustomData: boolean
}): string {
    const fmt = validateHoverYFormat(options.yFormat)
    const unitPart = options.unit.trim() ? ` ${options.unit.trim()}` : ""
    const base = `<b>%{y:${fmt}}</b>${unitPart}<extra>%{meta[0]}</extra>`
    if (options.hasCustomData) {
        return `${base}<br>%{customdata}`
    }
    return base
}

/** Tooltip avec date au format jour/mois/année (séries temporelles quotidiennes). */
export function buildHistoriquePuissanceHoverTemplate(options: {
    yFormat: string
    unit: string
}): string {
    const fmt = validateHoverYFormat(options.yFormat)
    const unitPart = options.unit.trim() ? ` ${options.unit.trim()}` : ""
    return `<b>%{x|%d/%m/%Y}</b><br><b>%{y:${fmt}}</b>${unitPart}<extra>%{meta[0]}</extra>`
}

export function mergeDapiHoverLayout(
    overrides: Record<string, unknown>
): Record<string, unknown> {
    const baseHl = DAPI_LIKE_HOVER_BASE.hoverlabel as {
        bgcolor: string
        bordercolor: string
        font: Record<string, unknown>
    }
    const ovHl = overrides.hoverlabel as
        | { font?: Record<string, unknown> }
        | undefined
    return {
        ...DAPI_LIKE_HOVER_BASE,
        ...overrides,
        hovermode: "x unified",
        hoverlabel: {
            ...baseHl,
            ...(ovHl ?? {}),
            font: {
                ...baseHl.font,
                ...ovHl?.font,
            },
        },
    }
}

export function ensureTraceDapiHover(
    trace: Record<string, unknown>,
    yUnitLabel: string
): Record<string, unknown> {
    const hasTemplate =
        typeof trace.hovertemplate === "string" &&
        trace.hovertemplate.length > 0
    if (hasTemplate) {
        return trace
    }
    const nm = typeof trace.name === "string" ? trace.name : ""
    const metaExisting = Array.isArray(trace.meta) ? trace.meta : []
    const meta = metaExisting.length > 0 ? metaExisting : [nm]
    const hasCustom = trace.customdata != null
    return {
        ...trace,
        meta,
        hovertemplate: buildTooltipChartTemplate({
            yFormat: DEFAULT_HOVER_Y_FORMAT,
            unit: yUnitLabel,
            hasCustomData: hasCustom,
        }),
    }
}

export function enrichTracesHover(
    traces: object[],
    yUnitLabel: string
): object[] {
    return traces.map((tr) =>
        ensureTraceDapiHover(tr as Record<string, unknown>, yUnitLabel)
    )
}

/**
 * Force libellés de survol cohérents pour l’historique puissance (série Plotly brute ou construite).
 */
export function enrichHistoriqueTraceHover(
    traces: object[],
    chart: PlotlyLayoutAxisChartMeta,
    yUnitLabel: string
): object[] {
    const isHistorique = chart.name?.toLowerCase() === "historique_puissance"
    if (!isHistorique) return traces
    const hovertemplate = buildHistoriquePuissanceHoverTemplate({
        yFormat: DEFAULT_HOVER_Y_FORMAT,
        unit: yUnitLabel,
    })
    return traces.map((tr) => {
        const t = tr as Record<string, unknown>
        const nm = typeof t.name === "string" ? t.name : ""
        const metaExisting = Array.isArray(t.meta) ? t.meta : []
        const meta = metaExisting.length > 0 ? metaExisting : [nm]
        return { ...t, meta, hovertemplate }
    })
}

export { DEFAULT_HOVER_Y_FORMAT }
